import { useState } from 'react';

import useGetStudentAnswerById from '../use-get-student-answer-by-id';
import DrawResponsePreview from './draw-response-preview';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { usePagePreviewIntersectionObserver } from 'approot/activity-builder/page/activity-builder-page-preview.hooks';
import Bouncer from 'approot/shared/bouncer/bouncer';
import * as Styled from './draw-response-individual-preview.style';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { DrawData } from 'approot/activity-builder/components/draw/draw.constants';
import { LanguageCodes } from 'global/constants';

type Props = {
  componentId: string;
  classId: number;
  uuid: string | null;
  studentName: string;
  imageUrl?: string | null;
  dimensionsData: {
    width: number;
    height: number;
  };
  questionData?: DrawData | null;
  language: LanguageCodes;
};

const DrawResponseIndividualPreview = ({
  componentId,
  classId,
  uuid,
  studentName,
  imageUrl,
  dimensionsData,
  questionData,
  language,
}: Props) => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  const { contentId, contentType } = useGetInteractiveLessonParams();
  const { isVisible } = usePagePreviewIntersectionObserver({
    containerElement: containerRef,
    parentElement: null,
  });

  const { data, loading } = useGetStudentAnswerById({
    skip: !isVisible || !uuid,
    classId,
    contentId,
    contentType,
    uuid: uuid || '', //should never be null
    fetchPolicy: 'cache-and-network',
  });

  const answerData = data?.getStudentAnswerById;

  return (
    <Styled.DrawResponseIndividualPreview ref={setContainerRef}>
      {loading ? (
        <Bouncer />
      ) : answerData ? (
        <DrawResponsePreview
          id={componentId}
          ariaLabel={`Student response for ${studentName}`}
          dimensionsData={dimensionsData}
          imageUrl={imageUrl}
          answerUpdatedAt={answerData.updatedAt}
          answerUuid={answerData.uuid}
          answer={answerData.answer as DrawAnswerFragment}
          questionData={questionData}
          language={language}
        />
      ) : null}
    </Styled.DrawResponseIndividualPreview>
  );
};

export default DrawResponseIndividualPreview;
